import { useEffect, useState } from 'react';
import { Link, Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getAdminKey } from '../lib/api';
import { signOut } from '../lib/identity';
import { useIdentity } from '../lib/use-identity';
import { FeedbackWidget } from './FeedbackWidget';
import { useToast } from './Toast';
import { identifySubscriber, syncSessionReplay } from '../lib/analytics';

const handledAuthStatuses = new Set<string>();

export function Layout() {
  const [isAdmin, setIsAdmin] = useState<boolean>(() => Boolean(getAdminKey()));
  const identity = useIdentity();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (identity) void identifySubscriber(identity);
  }, [identity]);

  useEffect(() => {
    syncSessionReplay(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => setIsAdmin(Boolean(getAdminKey()));
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('admin-key-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('admin-key-changed', sync);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authStatus = params.get('auth');
    if (!authStatus) return;

    const key = `${location.pathname}${location.search}${location.hash}`;
    if (!handledAuthStatuses.has(key)) {
      handledAuthStatuses.add(key);
      if (authStatus === 'lenny_cancelled') {
        toast.info('Sign-in cancelled', 'You can try Lenny Access again whenever you are ready.');
      } else if (authStatus === 'lenny_link_expired') {
        toast.error('Sign-in link expired', 'Start Lenny Access again to get a fresh sign-in link.');
      } else if (authStatus === 'lenny_failed') {
        toast.error('Sign-in failed', 'Please try Lenny Access again.');
      }
    }

    params.delete('auth');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate, toast]);

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <div role="status" className="border-b border-amber-200 bg-amber-50 text-amber-950">
        <div className="container-page flex flex-col gap-1 py-3 text-sm sm:flex-row sm:gap-2">
          <strong>Login is temporarily disabled.</strong>
          <span>Access should be restored soon.</span>
        </div>
      </div>
      <header className="sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur">
        <div className="container-page flex items-center justify-between py-3.5">
          <Link to="/" className="flex items-center gap-2.5 font-semibold tracking-tight">
            <LennyMark />
            <span className="text-[15px]">Lenny's Workshops</span>
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            {identity ? <IdentityChip name={identity.name} onSignOut={signOut} /> : null}
            {isAdmin ? (
              <div className="flex items-center gap-1">
                <NavLink to="/" end className={({ isActive }) => 'btn-ghost ' + (isActive ? 'text-ink' : '')}>
                  Workshops
                </NavLink>
                <NavLink to="/admin" className={({ isActive }) => 'btn-ghost ' + (isActive ? 'text-ink' : '')}>
                  Admin
                </NavLink>
                <NavLink to="/admin/crm" className={({ isActive }) => 'btn-ghost ' + (isActive ? 'text-ink' : '')}>
                  CRM
                </NavLink>
                <NavLink to="/admin/feedback" className={({ isActive }) => 'btn-ghost ' + (isActive ? 'text-ink' : '')}>
                  Reports
                </NavLink>
              </div>
            ) : null}
          </nav>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <FeedbackWidget />
    </div>
  );
}

function IdentityChip({ name, onSignOut }: { name: string; onSignOut: () => void }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span data-private className="hidden sm:inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-2.5 text-xs">
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-white">
        {initials || '?'}
      </span>
      <span className="text-ink-muted">{name}</span>
      <button
        type="button"
        onClick={onSignOut}
        className="text-ink-subtle hover:text-ink"
        title="Sign out"
      >
        ✕
      </button>
    </span>
  );
}

function LennyMark() {
  return (
    <img
      src="/lennys-newsletter-logo.png"
      alt=""
      width={28}
      height={28}
      className="h-7 w-7 rounded-full object-cover"
      aria-hidden
    />
  );
}
